export interface EmbeddedContentPolicyOptions {
  collapseBinaryContent?: boolean;
  maxInlineTextLength?: number;
}

export interface EmbeddedContentPreview {
  collapsed: boolean;
  displayValue: string;
  reason?: 'binary' | 'large-value';
  sizeInBytes?: number;
  mimeCode?: string;
  filename?: string;
}

const DEFAULT_OPTIONS: Required<EmbeddedContentPolicyOptions> = {
  collapseBinaryContent: true,
  maxInlineTextLength: Number.POSITIVE_INFINITY
};

export function getEmbeddedContentPreview(value: unknown, fieldNode: unknown, options?: EmbeddedContentPolicyOptions): EmbeddedContentPreview | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  const resolvedOptions = { ...DEFAULT_OPTIONS, ...options };
  const node = fieldNode != null && typeof fieldNode === 'object' ? fieldNode as Record<string, unknown> : undefined;
  if (resolvedOptions.collapseBinaryContent && isEmbeddedBinaryNode(node)) {
    const sizeInBytes = estimateBase64Size(value);
    return {
      collapsed: true,
      displayValue: `[base64 content hidden${sizeInBytes ? `, ${formatBytes(sizeInBytes)}` : ''}]`,
      reason: 'binary',
      sizeInBytes,
      mimeCode: readString(node?.['MimeCode'] ?? node?.['mimeCode']),
      filename: readString(node?.['Filename'] ?? node?.['filename'])
    };
  }

  if (value.length > resolvedOptions.maxInlineTextLength) {
    return {
      collapsed: true,
      displayValue: `[large value hidden, ${formatBytes(value.length)}]`,
      reason: 'large-value',
      sizeInBytes: value.length
    };
  }

  return undefined;
}

export function isEmbeddedBinaryNode(node: Record<string, unknown> | undefined): boolean {
  if (!node) {
    return false;
  }

  const provenance = node['$provenance'] as Record<string, unknown> | undefined;
  const term = readString(provenance?.['term']);
  const termPath = readString(provenance?.['termPath']);
  if (term === 'BT-125' || termPath?.includes('BT-125')) {
    return true;
  }

  return typeof node['Content'] === 'string' && (
    typeof node['MimeCode'] === 'string' ||
    typeof node['Filename'] === 'string' ||
    typeof node['mimeCode'] === 'string' ||
    typeof node['filename'] === 'string' ||
    typeof node['encodingCode'] === 'string'
  );
}

function estimateBase64Size(value: string): number | undefined {
  const normalized = value.replace(/\s/g, '');
  if (normalized.length === 0) {
    return undefined;
  }

  const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((normalized.length * 3) / 4) - padding);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  for (const unit of units) {
    if (value < 1024) {
      return `${value.toFixed(value >= 10 ? 0 : 1)} ${unit}`;
    }
    value = value / 1024;
  }

  return `${value.toFixed(1)} TB`;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}